import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { TableHeader, ProgressBar, Spinner } from "@services/ui-components";
import { getSteps, IStep } from "@me-team/common/src/components/Me-menu/MenuItemsPage/constants";
import QrMenuPreview from "../MenuViewPage/components/QrMenuPreview";
import PublishedMenuImg1 from "@me-team/common/src/images/menu/published-menu_1.jpg";
import PublishedMenuImg2 from "@me-team/common/src/images/menu/published-menu_2.jpg";
import PublishedMenuImg3 from "@me-team/common/src/images/menu/published-menu_3.jpg";
import PublishedMenuImg4 from "@me-team/common/src/images/menu/published-menu_4.jpg";
import PublishedMenuImg5 from "@me-team/common/src/images/menu/published-menu_5.jpg";
import PublishedMenuImg6 from "@me-team/common/src/images/menu/published-menu_6.jpg";
import PublishedMenuImg7 from "@me-team/common/src/images/menu/published-menu_7.jpg";
import { useLinkAndQrCodeMenu } from "../MenuViewPage/hooks/useLinkAndQrCodeMenu";
import { ApolloError } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";

const PublishedMenuPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const steps: IStep[] = getSteps(t);

    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number | null>(null);

    const { menu, qrMenuQrCodeLink, qrMenuLink, linkAndQrCodeMenuLoading } = useLinkAndQrCodeMenu({
        branchId,
        menuId: null,
        onError: (apolloError: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(apolloError));
        },
    });

    const menuType = menu?.type?.name;

    if (linkAndQrCodeMenuLoading) return <Spinner />;

    return (
        <div className="mb-9 px-2 px-lg-0">
            <TableHeader
                title={t("Menu published")}
                paths={[
                    { path: `/branches/${branchId}`, label: t("Branches") },
                    { path: `/menu/all-menus/branch/${branchId}`, label: t("All menu") },
                    { path: "", label: t("Menu published") },
                ]}
                colSize={7}
                btnColSize={5}
            ></TableHeader>
            <ProgressBar currentStep={4} steps={steps} marginBottom="mb-0" isMenu />
            <div className="py-5 px-4 mt-4 bg-violet rounded-20">
                <h3 className="fs-4 text-center">
                    {t("The new {{menuType}} has been published!", { menuType: t(menuType) })}
                </h3>
                <Col lg={10} className="mx-lg-auto">
                    <p className="text-grey text-center">
                        {t(
                            "Thank you for using our platform to create an online menu for your venue. You have completed the process of developing and setting up your unique menu, and now you are ready to share it with your customers."
                        )}
                    </p>
                </Col>
                <QrMenuPreview
                    qrMenuQrCodeLink={qrMenuQrCodeLink}
                    qrMenuLink={qrMenuLink}
                    menuType={menuType}
                    isPublishedPage
                />
                <h3 className="fs-24 text-center mb-4">{t("Where to Use the QR Code of Your Venue’s Menu")}</h3>
                <Container>
                    <Row className="flex-column-reverse flex-lg-row mb-4">
                        <Col xs={12} lg={6}>
                            <div className="d-flex flex-column justify-content-center bg-white rounded-20 p-4 px-lg-5 h-100">
                                <h4 className="fs-5 mb-2">{t("On the tables of your establishment")}</h4>
                                <p className="text-grey mb-0">
                                    {t(
                                        "Place QR codes on the tables so your customers can easily scan them and view your menu."
                                    )}
                                </p>
                            </div>
                        </Col>
                        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
                            <img
                                src={PublishedMenuImg1}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code placed on a table of your establishment")}
                                loading="lazy"
                            />
                        </Col>
                    </Row>
                    <Row className="d-none d-lg-flex mb-4">
                        <Col lg={6}>
                            <img
                                src={PublishedMenuImg2}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code displayed on a phone")}
                                loading="lazy"
                            />
                        </Col>
                        <Col lg={6}>
                            <img
                                src={PublishedMenuImg3}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code on a counter or interior surface")}
                                loading="lazy"
                            />
                        </Col>
                    </Row>
                    <Row className="mb-4">
                        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
                            <img
                                src={PublishedMenuImg4}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code on entrance doors")}
                                loading="lazy"
                            />
                        </Col>
                        <Col xs={12} lg={6}>
                            <div className="d-flex flex-column justify-content-center bg-white rounded-20 p-4 px-lg-5 h-100">
                                <h4 className="fs-5 mb-2">{t("On windows and entrance doors")}</h4>
                                <p className="text-grey mb-0">
                                    {t(
                                        "Attract new customers by placing QR codes on the windows or entrance doors of your venue so they can explore your offerings before entering."
                                    )}
                                </p>
                            </div>
                        </Col>
                    </Row>
                    <Row className="d-none d-lg-flex mb-4">
                        <Col lg={6}>
                            <img
                                src={PublishedMenuImg7}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code on a counter or interior surface")}
                                loading="lazy"
                            />
                        </Col>
                        <Col lg={6}>
                            <img
                                src={PublishedMenuImg5}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code used on promotional materials")}
                                loading="lazy"
                            />
                        </Col>
                    </Row>
                    <Row className="flex-column-reverse flex-lg-row">
                        <Col xs={12} lg={6}>
                            <div className="d-flex flex-column justify-content-center bg-white rounded-20 p-4 px-lg-5 h-100">
                                <h4 className="fs-5 mb-2">{t("On promotional materials")}</h4>
                                <p className="text-grey mb-0">
                                    {t(
                                        "Use QR codes on promotional banners, flyers, or other marketing materials to draw customers’ attention to your online menu."
                                    )}
                                </p>
                            </div>
                        </Col>
                        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
                            <img
                                src={PublishedMenuImg6}
                                className="w-100 h-100 object-fit-cover rounded-20"
                                alt={t("QR code printed on a flyer or banner")}
                                loading="lazy"
                            />
                        </Col>
                    </Row>
                </Container>
            </div>

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default PublishedMenuPage;
